import { getAccessToken } from "../../utils/auth";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, CircularProgress } from "@mui/material";
import axios from "../../utils/api.js";

const DaranswerData = () => {
  const [distributors, setDistributors] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState("");
  const [answers, setAnswers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const totalPages = answers.length;
  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;

  useEffect(() => {
    const fetchDistributors = async () => {
      try {
        const response = await axios.get("/distributors", {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        setDistributors(response.data);
      } catch (err) {
        console.error("Error fetching distributors:", err);
        setError("Failed to load distributors");
      }
    };

    fetchDistributors();
  }, []);

  const fetchAnswers = useCallback(async () => {
    if (!selectedDistributor) {
      setError("Please select a distributor");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = getAccessToken();
      if (!token) {
        setError("Unauthorized: No token found");
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `/answers-by-distributor/${selectedDistributor}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setAnswers(response.data);
        setCurrentPage(1);
      } else {
        setError(response.data.message || "Failed to fetch answers");
      }
    } catch (err) {
      setError("No Data For This Distributor");
    } finally {
      setLoading(false);
    }
  }, [selectedDistributor]);

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  const tableHeaderStyle = {
    padding: "12px 16px",
    textAlign: "left",
    fontSize: 13,
    fontWeight: 600,
  };

  const tableCellStyle = {
    padding: "12px 16px",
    fontSize: 14,
    color: "#334155",
    borderBottom: "1px solid #e2e8f0",
  };

  if (loading) {
    return (
      <div
        style={{
          ...pageStyle,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <CircularProgress size={48} sx={{ color: "#667eea" }} />
        <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Select Distributor & View DAR Analysis
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          Choose a distributor to view full answers and weightage
        </p>
      </div>

      <div
        style={{
          maxWidth: 720,
          margin: "0 auto 24px",
          display: "flex",
          gap: 12,
          alignItems: "flex-end",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <select
          value={selectedDistributor}
          onChange={(e) => setSelectedDistributor(e.target.value)}
          style={{
            padding: "0.65rem 2.25rem 0.65rem 0.85rem",
            fontSize: "1rem",
            minWidth: 260,
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            outline: "none",
            backgroundColor: "#fff",
            appearance: "none",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%2364748b' height='24' viewBox='0 0 24 24' width='24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 0.75rem center",
            backgroundSize: "1.25rem",
            cursor: "pointer",
          }}
        >
          <option value="">Select Distributor</option>
          {distributors.map((distributor, index) => (
            <option key={index} value={distributor.name}>
              {distributor.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={fetchAnswers}
          style={{
            padding: "0.65rem 1.25rem",
            fontSize: "1rem",
            fontWeight: 600,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
          }}
        >
          Search
        </button>
      </div>

      {error && (
        <p
          style={{
            textAlign: "center",
            color: "#dc2626",
            fontSize: "0.95rem",
            margin: "0 0 20px",
          }}
        >
          {error}
        </p>
      )}

      {answers.length > 0 && (
        <>
          <div
            style={{
              maxWidth: 1000,
              margin: "0 auto 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <span
              style={{
                fontSize: "1rem",
                fontWeight: 600,
                color: "#1e293b",
              }}
            >
              Submission {currentPage}
              {" --- > "}
              {answers[currentPage - 1]?.submittedAt
                ? answers[currentPage - 1].submittedAt.split("T")[0]
                : "—"}
              {" --- > "}
              Page {currentPage}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={!hasPrev}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  background: hasPrev
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "#e2e8f0",
                  color: hasPrev ? "#fff" : "#94a3b8",
                  border: "none",
                  borderRadius: 8,
                  cursor: hasPrev ? "pointer" : "not-allowed",
                  boxShadow: hasPrev
                    ? "0 2px 8px rgba(102, 126, 234, 0.3)"
                    : "none",
                }}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={!hasNext}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  background: hasNext
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "#e2e8f0",
                  color: hasNext ? "#fff" : "#94a3b8",
                  border: "none",
                  borderRadius: 8,
                  cursor: hasNext ? "pointer" : "not-allowed",
                  boxShadow: hasNext
                    ? "0 2px 8px rgba(102, 126, 234, 0.3)"
                    : "none",
                }}
              >
                Next
              </button>
            </div>
          </div>
          {(() => {
            const submissionIndex = currentPage - 1;
            const submission = answers[submissionIndex];
            if (!submission) return null;
            const totalWeightage =
            (submission.decisionMakingAuthority || []).reduce(
              (total, item) => total + (item.weightage || 0),
              0
            ) +
            (submission.abilityToPurchase || []).reduce(
              (total, item) => total + (item.weightage || 0),
              0
            ) +
            (submission.readinessToBuy || []).reduce(
              (total, item) => total + (item.weightage || 0),
              0
            );

          const submittedDate = submission.submittedAt
            ? submission.submittedAt.split("T")[0]
            : "—";

          return (
            <div
              key={submissionIndex}
              style={{
                maxWidth: 1000,
                margin: "0 auto 32px",
                backgroundColor: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: 24,
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              }}
            >
              <h3
                style={{
                  margin: "0 0 8px",
                  fontSize: "1.15rem",
                  fontWeight: 600,
                  color: "#1e293b",
                }}
              >
                Submission {submissionIndex + 1} – {submission.distributorName} –{" "}
                {submittedDate}
              </h3>
              <p
                style={{
                  margin: "0 0 20px",
                  fontSize: "0.95rem",
                  color: "#64748b",
                  fontWeight: 600,
                }}
              >
                Total Weightage: {totalWeightage}
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
                {submission.answers && submission.answers.length > 0 && (
                  <div>
                    <h4
                      style={{
                        margin: "0 0 12px",
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: "#1e293b",
                      }}
                    >
                      Answers
                    </h4>
                    <div
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        overflow: "hidden",
                      }}
                    >
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                        }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: "#1e3a5f", color: "#fff" }}>
                            <th style={tableHeaderStyle}>Question No.</th>
                            <th style={tableHeaderStyle}>Question</th>
                            <th style={tableHeaderStyle}>Answer</th>
                            <th style={tableHeaderStyle}>Weightage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {submission.answers.map((answer, idx) => (
                            <tr
                              key={idx}
                              style={{
                                backgroundColor:
                                  idx % 2 === 0 ? "#fff" : "#f8fafc",
                              }}
                            >
                              <td style={tableCellStyle}>
                                {answer.questionNumber}
                              </td>
                              <td style={tableCellStyle}>{answer.question}</td>
                              <td style={tableCellStyle}>{answer.answer}</td>
                              <td style={tableCellStyle}>{answer.weightage}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {submission.decisionMakingAuthority &&
                  submission.decisionMakingAuthority.length > 0 && (
                    <div>
                      <h4
                        style={{
                          margin: "0 0 12px",
                          fontSize: "1rem",
                          fontWeight: 600,
                          color: "#1e293b",
                        }}
                      >
                        Decision Making Authority
                      </h4>
                      <div
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          overflow: "hidden",
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#1e3a5f",
                                color: "#fff",
                              }}
                            >
                              <th style={tableHeaderStyle}>Question No.</th>
                              <th style={tableHeaderStyle}>Question</th>
                              <th style={tableHeaderStyle}>Answer</th>
                              <th style={tableHeaderStyle}>Weightage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {submission.decisionMakingAuthority.map(
                              (answer, idx) => (
                                <tr
                                  key={idx}
                                  style={{
                                    backgroundColor:
                                      idx % 2 === 0 ? "#fff" : "#f8fafc",
                                  }}
                                >
                                  <td style={tableCellStyle}>
                                    {answer.questionNumber}
                                  </td>
                                  <td style={tableCellStyle}>
                                    {answer.question}
                                  </td>
                                  <td style={tableCellStyle}>
                                    {answer.answer}
                                  </td>
                                  <td style={tableCellStyle}>
                                    {answer.weightage}
                                  </td>
                                </tr>
                              )
                            )}
                            <tr
                              style={{
                                backgroundColor: "#667eea",
                                color: "#fff",
                                fontWeight: 600,
                              }}
                            >
                              <td
                                style={{
                                  ...tableCellStyle,
                                  color: "#fff",
                                  borderBottom: "none",
                                }}
                                colSpan={3}
                              >
                                Total Weightage
                              </td>
                              <td
                                style={{
                                  ...tableCellStyle,
                                  color: "#fff",
                                  borderBottom: "none",
                                }}
                              >
                                {submission.decisionMakingAuthority.reduce(
                                  (total, item) =>
                                    total + (item.weightage || 0),
                                  0
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                {submission.abilityToPurchase &&
                  submission.abilityToPurchase.length > 0 && (
                    <div>
                      <h4
                        style={{
                          margin: "0 0 12px",
                          fontSize: "1rem",
                          fontWeight: 600,
                          color: "#1e293b",
                        }}
                      >
                        Ability to Purchase
                      </h4>
                      <div
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          overflow: "hidden",
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#1e3a5f",
                                color: "#fff",
                              }}
                            >
                              <th style={tableHeaderStyle}>Question No.</th>
                              <th style={tableHeaderStyle}>Question</th>
                              <th style={tableHeaderStyle}>Answer</th>
                              <th style={tableHeaderStyle}>Weightage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {submission.abilityToPurchase.map((answer, idx) => (
                              <tr
                                key={idx}
                                style={{
                                  backgroundColor:
                                    idx % 2 === 0 ? "#fff" : "#f8fafc",
                                }}
                              >
                                <td style={tableCellStyle}>
                                  {answer.questionNumber}
                                </td>
                                <td style={tableCellStyle}>
                                  {answer.question}
                                </td>
                                <td style={tableCellStyle}>
                                  {answer.answer}
                                </td>
                                <td style={tableCellStyle}>
                                  {answer.weightage}
                                </td>
                              </tr>
                            ))}
                            <tr
                              style={{
                                backgroundColor: "#667eea",
                                color: "#fff",
                                fontWeight: 600,
                              }}
                            >
                              <td
                                style={{
                                  ...tableCellStyle,
                                  color: "#fff",
                                  borderBottom: "none",
                                }}
                                colSpan={3}
                              >
                                Total Weightage
                              </td>
                              <td
                                style={{
                                  ...tableCellStyle,
                                  color: "#fff",
                                  borderBottom: "none",
                                }}
                              >
                                {submission.abilityToPurchase.reduce(
                                  (total, item) =>
                                    total + (item.weightage || 0),
                                  0
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                {submission.readinessToBuy &&
                  submission.readinessToBuy.length > 0 && (
                    <div>
                      <h4
                        style={{
                          margin: "0 0 12px",
                          fontSize: "1rem",
                          fontWeight: 600,
                          color: "#1e293b",
                        }}
                      >
                        Readiness to Buy
                      </h4>
                      <div
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          overflow: "hidden",
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#1e3a5f",
                                color: "#fff",
                              }}
                            >
                              <th style={tableHeaderStyle}>Question No.</th>
                              <th style={tableHeaderStyle}>Question</th>
                              <th style={tableHeaderStyle}>Answer</th>
                              <th style={tableHeaderStyle}>Weightage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {submission.readinessToBuy.map((answer, idx) => (
                              <tr
                                key={idx}
                                style={{
                                  backgroundColor:
                                    idx % 2 === 0 ? "#fff" : "#f8fafc",
                                }}
                              >
                                <td style={tableCellStyle}>
                                  {answer.questionNumber}
                                </td>
                                <td style={tableCellStyle}>
                                  {answer.question}
                                </td>
                                <td style={tableCellStyle}>
                                  {answer.answer}
                                </td>
                                <td style={tableCellStyle}>
                                  {answer.weightage}
                                </td>
                              </tr>
                            ))}
                            <tr
                              style={{
                                backgroundColor: "#667eea",
                                color: "#fff",
                                fontWeight: 600,
                              }}
                            >
                              <td
                                style={{
                                  ...tableCellStyle,
                                  color: "#fff",
                                  borderBottom: "none",
                                }}
                                colSpan={3}
                              >
                                Total Weightage
                              </td>
                              <td
                                style={{
                                  ...tableCellStyle,
                                  color: "#fff",
                                  borderBottom: "none",
                                }}
                              >
                                {submission.readinessToBuy.reduce(
                                  (total, item) =>
                                    total + (item.weightage || 0),
                                  0
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          );
          })()}
        </>
      )}
    </div>
  );
};

export default DaranswerData;
